import React, { useState } from 'react';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { Phone, MessageCircle } from 'lucide-react';
import { FAQ_DATA } from '../../data/faqData';
import { CONTACT } from '../../config/contact';

// Questions flow as one numbered list — no category headings. Numbers run in
// reading order down each column (1…n down column one, then the next column).
const FLAT_ITEMS = FAQ_DATA.flatMap((section, sectionIndex) =>
  section.items.map((faq, itemIndex) => ({
    ...faq,
    key: `${sectionIndex}-${itemIndex}`,
    hoverColor: section.hoverColor,
  }))
).map((faq, index) => ({ ...faq, number: index + 1 }));

// Split items into `count` columns, filling earlier columns first so the
// lengths stay as even as possible (e.g. 19 items → 7 / 6 / 6).
const splitIntoColumns = (items, count) => {
  if (count <= 1) return [items];
  const base = Math.floor(items.length / count);
  const remainder = items.length % count;
  const columns = [];
  let cursor = 0;
  for (let i = 0; i < count; i += 1) {
    const size = base + (i < remainder ? 1 : 0);
    columns.push(items.slice(cursor, cursor + size));
    cursor += size;
  }
  return columns;
};

// Shared FAQ accordion. Used by the standalone /faq page (three columns) and
// the FAQ modal (single column) so both stay visually and behaviourally identical.
const FaqAccordion = ({ columnCount = 1 }) => {
  const [expanded, setExpanded] = useState(null);

  const columns = splitIntoColumns(FLAT_ITEMS, columnCount);
  const gridClass = columnCount === 3
    ? 'grid grid-cols-1 lg:grid-cols-3 gap-x-4 gap-y-2.5 items-start'
    : columnCount === 2
      ? 'grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-2.5 items-start'
      : 'space-y-2.5';

  const renderItem = (faq) => {
    const isOpen = expanded === faq.key;
    return (
      <div
        key={faq.key}
        className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200/80 dark:border-gray-800 shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md hover:border-teal-200 dark:hover:border-teal-800"
      >
        <button
          onClick={() => setExpanded(isOpen ? null : faq.key)}
          className="w-full flex items-start gap-2.5 px-3 py-2.5 text-left group"
        >
          {/* Number tile — ModuleGuard styling */}
          <div className="w-8 h-8 rounded-lg bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center flex-shrink-0 text-xs font-bold tabular-nums text-teal-600 dark:text-teal-400">
            {String(faq.number).padStart(2, '0')}
          </div>

          <h3 className={`flex-1 min-w-0 text-[13px] font-normal leading-snug transition-colors ${isOpen ? 'text-teal-600 dark:text-teal-400' : `text-gray-900 dark:text-white ${faq.hoverColor}`}`}>
            {faq.q}
          </h3>

          <svg
            className={`w-4 h-4 mt-2 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div className={`grid transition-all duration-200 ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
          <div className="overflow-hidden">
            <p className="px-3 pb-3 text-[13px] leading-relaxed text-gray-600 dark:text-gray-400">
              {faq.a}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={gridClass}>
        {columns.map((column, ci) => (
          <div key={ci} className="space-y-2.5 min-w-0">
            {column.map(renderItem)}
          </div>
        ))}
      </div>

      {/* Still have questions — compact CTA */}
      <div className="mt-4 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200/80 dark:border-gray-800 shadow-sm px-5 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center flex-shrink-0">
            <MessageCircle className="w-5 h-5 text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900 dark:text-white">Still have questions?</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">We answer within hours.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={CONTACT.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold transition-colors"
          >
            <Phone className="w-3.5 h-3.5" />
            WhatsApp
          </a>
          <a
            href={`mailto:${CONTACT.email}`}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <EnvelopeIcon className="w-3.5 h-3.5" />
            Email
          </a>
        </div>
      </div>
    </>
  );
};

export default FaqAccordion;
